import type { Asset } from "@shared/types/asset";
import Accordion, { ACCORDION_VARIANTS } from "@shared/ui/Accordion";
import TextEditor from "@shared/ui/TextEditor";
import React, { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { BoxExplorer } from "react-box-explorer";
import "react-box-explorer/style.css";

import { useBoxBaseUrls } from "~/hooks";
import { useSidebarStore } from "@shared/ui/SidebarNavigation";
import DocumentationDetachmentStatus from "$/settings/enums/machine/template-documentation-status.json";

import CustomFieldsAccordion from "~/components/customFields/_customFieldsAccordion";
import getEnum from "$/settings/enums";
import PAID_FEATURES from "$/settings/paid-features.json";
import SubAssets from "~/components/assets/SubAssets";
import ContentLoading from "~/components/ContentLoading";
import { Loading, useBoxTokenRefresh } from "~/components/general";
import { LoginAlert } from "~/components/LoginAlert";
import MachineParts from "~/components/machines/MachineParts";
import PreventiveMaintenance from "~/components/preventiveMaintenance";
import UnderlineTabsWrapper from "~/components/UnderlineTabsWrapper";
import {
  SHARED_ASSETS_GI,
  SHARED_ASSETS_VIEW,
  SHARED_ASSETS_VIEW_DOCS,
  SHARED_ASSETS_VIEW_HISTORY,
  SHARED_ASSETS_VIEW_PARTS,
  SHARED_ASSETS_VIEW_PME,
  SHARED_ASSETS_VIEW_SUB_ASSETS,
} from "~/constants";
import { MachineHistory } from "~/containers/machine/MachineHistory";
import { PMEMessageProvider } from "~/containers/plannedEvents/helpers";
import { useListSharedPreventiveMaintenanceEvents } from "~/services";
import {
  useGetAssetFolderTokens,
  useGetAssetsPartsById,
  useGetAssetWithOrgById,
} from "~/services/asset";
import { AssetType } from "~/types/assetType";
import { registerMixpanelEvent } from "~/utils/_mixpanel";
import { useGetAppConfigValue } from "~/utils/appFeatures";
import isPaidFeatureAvailable from "~/utils/isPaidFeatureAvailable";

const paidFeatures = getEnum(PAID_FEATURES, "reference");

const SingleSharedAssetTabs = ({
  asset,
  assetParts,
  isQRCodeFlow,
  setShowQRModal,
  onAssetClick,
  onRequestClick,
  connectionId: connectionIdProp,
}: {
  asset: AssetType;
  assetParts;
  isQRCodeFlow: boolean;
  setShowQRModal: (val: Asset) => void;
  /** Optional callback for asset click. When provided, replaces default Link navigation. */
  onAssetClick?: (assetId: string, assetName: string) => void;
  /** Optional callback for request click. When provided, replaces default Link navigation. */
  onRequestClick?: (
    requestId: string,
    requestTitle: string,
    isSupportTicket?: boolean,
  ) => void;
  /** The portal being viewed; decides which sharing grants apply to this asset. */
  connectionId?: string;
}) => {
  const { boxBaseUrls } = useBoxBaseUrls();

  const { assetId, connectionId: connectionIdParam } = useParams() as {
    connectionId: string;
    assetId: string;
  };
  // See the note on `SingleSharedAsset`: the customer portal's route names this
  // param `id`, so it passes the value down instead.
  const connectionId = connectionIdProp ?? connectionIdParam;

  const sidebarExpanded = useSidebarStore((s) => s.expanded);
  const { messages } = useIntl();

  const [showLoginAlert, setShowLoginAlert] = React.useState(false);
  const appConfig = useGetAppConfigValue();

  // eslint-disable-next-line prefer-const
  let { loading: assetDataLoading, asset: assetData } = useGetAssetWithOrgById({
    id: assetId,
    connectionId,
    skipCondition: !!asset?._id,
  });

  assetData = assetData || asset;

  const sharedOrganization = assetData?.oem;

  const assetAccess = assetData?.sharedAssetAccess;

  const isSharedOrgPMEPaid = isPaidFeatureAvailable(
    paidFeatures.preventiveMaintenance,
    // @ts-ignore
    { oem: sharedOrganization },
    appConfig,
    false,
  );

  const isCreatedFromTemplate = !!assetData.template;
  const isAttachedToProduct =
    isCreatedFromTemplate &&
    assetData.detachedFromTemplate.documentation ===
      DocumentationDetachmentStatus.attached;

  const externalFolderIds = isAttachedToProduct
    ? [
        assetData?.documentFolders?.externalId,
        assetData?.template?.documentFolders?.externalId,
      ]
    : [assetData?.documentFolders?.externalId];

  useEffect(() => {
    Boolean(assetData?.oem?.slug) &&
      registerMixpanelEvent(SHARED_ASSETS_VIEW, {
        sharerOrgUrl: assetData?.oem?.slug,
      });
  }, [assetData?.oem?.slug]);

  const onChangeTab = (title) => {
    let event = null;
    if (
      title.includes(
        messages?.machines?.["machineDetails"].titleTabs.generalInfo,
      )
    ) {
      event = SHARED_ASSETS_GI;
    } else if (
      title.includes(
        messages?.machines?.["machineDetails"].titleTabs.documentation,
      )
    ) {
      event = SHARED_ASSETS_VIEW_DOCS;
    } else if (
      title.includes(messages["machines"]["machineDetails"].titleTabs.subAssets)
    ) {
      event = SHARED_ASSETS_VIEW_SUB_ASSETS;
    } else if (
      title.includes(messages?.machines?.["machineDetails"].titleTabs.parts)
    ) {
      event = SHARED_ASSETS_VIEW_PARTS;
    } else if (
      title.includes(
        messages?.machines?.["machineDetails"].titleTabs.preventiveMaintenance,
      )
    ) {
      event = SHARED_ASSETS_VIEW_PME;
    } else if (messages["machines"]["machineDetails"].titleTabs.history) {
      event = SHARED_ASSETS_VIEW_HISTORY;
    }

    if (event !== null) {
      registerMixpanelEvent(event, {
        sharerOrgUrl: assetData?.oem?.slug,
      });
    }
  };

  // eslint-disable-next-line prefer-const
  let { inventoryParts, inventoryPartsLoading } = useGetAssetsPartsById({
    id: assetId,
    isSharedAsset: true,
    skipCondition: !assetAccess?.parts || isQRCodeFlow,
  });

  inventoryParts = inventoryParts || assetParts;

  const {
    token,
    tokenMap,
    loading: folderTokenLoading,
    refetch: refetchAssetFolderTokens,
  } = useGetAssetFolderTokens({
    id: assetId,
    skipCondition: !assetAccess?.documentation || isQRCodeFlow,
  });

  useBoxTokenRefresh(refetchAssetFolderTokens);

  const { events, loading: loadingEvents } =
    useListSharedPreventiveMaintenanceEvents(
      {
        where: {
          oem_in: assetData?.oem,
          machine_in: assetId,
        },
      },
      !assetData?.oem || !assetAccess?.preventiveMaintenance,
    );

  const customFields = useMemo(() => {
    const filteredCustomFields = assetData?.customFields?.filter(
      (field) =>
        field?.fieldId?.visibilityScope === "external" &&
        ((field?.value !== null &&
          field?.value !== undefined &&
          field?.value !== "") ||
          field?.values?.length > 0),
    );

    filteredCustomFields?.sort((a, b) => {
      return (a?.fieldId?.order || 0) - (b?.fieldId?.order || 0);
    });

    return (
      filteredCustomFields?.map((field) => ({
        ...field,
        ...field.fieldId,
      })) ?? []
    );
  }, [assetData]);

  if (
    !sharedOrganization?.name ||
    !assetData?.name ||
    (!isQRCodeFlow && assetDataLoading)
  ) {
    return <Loading />;
  }

  if (
    !sharedOrganization?.name ||
    !assetData?.name ||
    (!isQRCodeFlow && assetDataLoading)
  ) {
    return <Loading />;
  }

  return (
    <div
      className={`flex flex-col w-full ${
        isQRCodeFlow ? "md:translate-y-0 -translate-y-[80px]" : ""
      }`}
      style={
        isQRCodeFlow
          ? { height: "calc(100vh - 300px)" }
          : {
              width: sidebarExpanded
                ? "calc(100vw - 528px)"
                : "calc(100vw - 355px)",
              transition: "width 0.2s",
            }
      }
    >
      <div className="md:px-2xl">
        <UnderlineTabsWrapper
          onChangeTab={onChangeTab}
          onRestrictedTabChange={() => setShowLoginAlert(true)}
          tabs={[
            {
              id: "general-information",
              label:
                messages?.machines?.["machineDetails"].titleTabs.generalInfo,
              content: (
                <div className="u-margin-b-0 flex flex-col gap-y-md">
                  <Accordion
                    defaultExpanded
                    variant={ACCORDION_VARIANTS.DESCRIPTION}
                    title={messages?.common?.["description"]}
                  >
                    <TextEditor
                      keyId="machine-desc"
                      className="p-sm hover:bg-accent"
                      content={assetData?.description}
                      readOnly
                    />
                  </Accordion>

                  {customFields.length !== 0 && (
                    <CustomFieldsAccordion
                      customFieldList={customFields}
                      isEditable={false}
                      title={messages?.sharedAssets?.["customFieldsAccordion"]}
                      hideEmptyFields
                      selectFieldValueClassname={""}
                      selectFieldLabelClassname={""}
                    />
                  )}
                </div>
              ),
            },
            ...(assetAccess?.documentation
              ? [
                  {
                    id: "documentation",
                    label:
                      messages?.machines?.["machineDetails"].titleTabs
                        .documentation,
                    content: folderTokenLoading ? (
                      <Loading />
                    ) : (
                      <BoxExplorer
                        {...boxBaseUrls}
                        folders={
                          !tokenMap && token
                            ? externalFolderIds.map((folderId) => ({
                                folderId,
                                token,
                                canUpload: false,
                                canCreateFolder: false,
                                canPreview: true,
                                canDownload: true,
                                canDelete: false,
                                canRename: false,
                              }))
                            : Object.entries(tokenMap || {}).map(
                                ([folderId, token]) => ({
                                  folderId,
                                  token,
                                  canUpload: false,
                                  canCreateFolder: false,
                                  canPreview: true,
                                  canDownload: true,
                                  canDelete: false,
                                  canRename: false,
                                }),
                              )
                        }
                      />
                    ),
                    restricted: isQRCodeFlow,
                  },
                ]
              : []),
            ...(assetAccess?.history
              ? [
                  {
                    id: "history",
                    label:
                      messages["machines"]["machineDetails"].titleTabs.history,
                    content: (
                      <MachineHistory
                        machine={assetData}
                        isSharedAsset
                        onRequestClick={onRequestClick}
                      />
                    ),
                    restricted: isQRCodeFlow,
                  },
                ]
              : []),
            {
              id: "sub-assets",
              label: `${
                messages["machines"]["machineDetails"].titleTabs.subAssets
              } (${assetData.totalChildrenCount || 0})`,
              content: (
                <SubAssets
                  asset={assetData}
                  isSharedAssetsPage
                  portalConnectionId={connectionId}
                  sharedAssetsOrg={sharedOrganization?._id}
                  isQRCodeEnabled={assetAccess?.qrCodes}
                  handleQRCodeClick={(asset) => {
                    setShowQRModal(asset);
                  }}
                  onAssetClick={onAssetClick}
                />
              ),
              restricted: isQRCodeFlow,
            },
            ...(assetAccess?.parts
              ? [
                  {
                    id: "parts",
                    label:
                      messages?.machines?.["machineDetails"].titleTabs.parts,
                    rightContent: inventoryPartsLoading ? (
                      <div className="loader" />
                    ) : (
                      `(${inventoryParts?.length || 0})`
                    ),
                    content: (
                      <>
                        {inventoryPartsLoading ? (
                          <ContentLoading />
                        ) : (
                          <MachineParts
                            parts={inventoryParts}
                            isDetached={
                              assetData?.["detachedFromTemplate"]
                                ?.inventoryParts
                            }
                            asset={asset}
                            isSharedAssetsPage
                          />
                        )}
                      </>
                    ),
                    restricted: isQRCodeFlow,
                  },
                ]
              : []),
            ...(assetAccess?.preventiveMaintenance && isSharedOrgPMEPaid
              ? [
                  {
                    id: "preventive-maintenance",
                    label: `${messages?.machines?.["machineDetails"].titleTabs
                      .preventiveMaintenance} (${events?.length || 0})`,
                    content: (
                      <PMEMessageProvider
                        messages={{
                          title:
                            messages?.preventiveMaintenance?.[
                              "noPreventiveMaintenanceTitle"
                            ],
                          description:
                            messages?.preventiveMaintenance?.[
                              "noPreventiveMaintenanceContent"
                            ],
                        }}
                      >
                        <PreventiveMaintenance
                          machineId={assetId}
                          events={events}
                          isLoadingEvents={loadingEvents}
                          isSharedAssetsPage
                        />
                      </PMEMessageProvider>
                    ),
                    restricted: isQRCodeFlow,
                  },
                ]
              : []),
          ]}
        />
      </div>
      {showLoginAlert && (
        <LoginAlert
          onClose={() => setShowLoginAlert(false)}
          portalSlug={sharedOrganization?.slug}
        />
      )}
    </div>
  );
};

export default SingleSharedAssetTabs;
